import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import slaService from '../../services/sla';
import { setPDPSlaMessages } from '../../slices/pdp';
import useDrupalSettings from '../useDrupalSettings';

import messages from './messages';

const convertSla = ({ sla, fallbackRange }) => ({
  sku: sla?.SKU_ID,
  fallbackRange,
  range: sla?.SLA_RANGE,
  wgRange: sla?.WG_SLA_RANGE,
});

export const SLA_PRODUCT_SOURCE = {
  SLA_PRODUCT_SOURCE_PRODUCT: 'product',
  SLA_PRODUCT_SOURCE_BUNDLE: 'bundle',
};

export const getGroupedSla = ({ skus, slaResponse, product }) => {
  if (slaResponse?.slaData.length) {
    const sla = slaResponse?.slaData.reduce(
      (acc, curr) => ({
        SKU_ID: +curr.SKU_ID > +acc.SKU_ID ? curr.SKU_ID : acc.SKU_ID,
        SLA: +curr.SLA > +acc.SLA ? curr.SLA : acc.SLA, // assuming this is always in the same metric (days or weeks)
        SLA_RANGE: +curr.SLA > +acc.SLA ? curr.SLA_RANGE : acc.SLA_RANGE, // assuming this is always in the same metric (days or weeks)
        WHITE_GLOVE:
          +curr.WHITE_GLOVE > +acc.WHITE_GLOVE
            ? curr.WHITE_GLOVE
            : acc.WHITE_GLOVE,
        WG_SLA_RANGE:
          +curr.WHITE_GLOVE > +acc.WHITE_GLOVE
            ? curr.WG_SLA_RANGE
            : acc.WG_SLA_RANGE,
      }),
      slaResponse?.slaData[0],
    );
    return [convertSla({ sla })];
  }

  const variant = product.variants.find((v) => v.sku === skus[0]);

  return [convertSla({ fallbackRange: variant.sla })];
};

export const getIndividualSla = ({
  skus,
  slaResponse,
  product,
  bedframeSelector,
}) =>
  skus.map((sku) => {
    const sla = slaResponse?.slaData.find(({ SKU_ID }) => SKU_ID === sku);
    if (sla && (sla?.SLA_RANGE || sla?.WG_SLA_RANGE)) {
      return convertSla({ sku, sla });
    }

    let variant = product?.variants.find((v) => v.sku === sku);

    if (!variant) {
      variant = bedframeSelector?.productParts
        ?.flatMap((part) => part.variants)
        .find((v) => v.sku === sku);
    }

    return convertSla({ sku, fallbackRange: variant?.sla });
  });

const useSlaService = (
  skus,
  isGroupingSla = false,
  productSource = SLA_PRODUCT_SOURCE.SLA_PRODUCT_SOURCE_PRODUCT,
  hasMultipleDeliveries = false,
) => {
  const skusAsJSON = JSON.stringify(skus);
  const [slaMessages, setSlaMessages] = useState([]);
  const {
    product: productFromDrupalSettings,
    bedframeSelector: bedframeSelectorFromDrupalSettings,
    bundle: bundleFromDrupalSettings,
  } = useDrupalSettings();

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  // TODO: Temporary solution - Definitive solution should be pass product variants to the hook by parameter
  const product = useMemo(() => {
    if (productSource === SLA_PRODUCT_SOURCE.SLA_PRODUCT_SOURCE_BUNDLE) {
      return {
        ...bundleFromDrupalSettings?.[0]?.product,
        variants: bundleFromDrupalSettings
          ?.map((bundleItem) => bundleItem.product.variants)
          .flat(),
      };
    }
    return productFromDrupalSettings;
  }, [bundleFromDrupalSettings, productFromDrupalSettings, productSource]);

  useEffect(() => {
    const slaMessage = slaMessages?.[0];
    /*
    const productKey = productFromDrupalSettings.key;

 
    const isRejuvenateProduct =
      REJUVENATE_COLLECTION_PRODUCT_KEYS.includes(productKey);

    const isPowerBaseProduct = PURPLE_PREMIUM_PLUS_SMART_BASE === productKey;

    DCP-5274 unified all the messaging across whiteGlove products */

    const isWhiteGlove =
      slaMessage?.wgRange && slaMessage?.wgRange === slaMessage?.range;

    let slaPrefix = formatMessage(messages.estimatedStandardMessage);

    if (
      isWhiteGlove
      /* && (isRejuvenateProduct || isPowerBaseProduct) 
      DCP-5274 unified all the messaging across whiteGlove products */
    ) {
      slaPrefix = formatMessage(messages.estimatedStandardMessage7to10Days);
    }

    dispatch(
      setPDPSlaMessages({
        slaMessages: [slaMessages] || [],
        slaPrefix,
      }),
    );
  }, [dispatch, formatMessage, productFromDrupalSettings.key, slaMessages]);

  // Checkout SLAs
  useEffect(() => {
    const retrieveSlaPerShipment = async (skusPerShipment) => {
      try {
        if (!skusPerShipment.length) {
          setSlaMessages([]);
        }
        const response = await slaService.getSlas(skusPerShipment);

        const slaData = isGroupingSla
          ? getGroupedSla({ skusPerShipment, slaResponse: response, product })
          : getIndividualSla({
              skusPerShipment,
              slaResponse: response,
              product,
              bedframeSelector: bedframeSelectorFromDrupalSettings,
            });
        return slaData;
      } catch (error) {
        return error;
      }
    };
    if (hasMultipleDeliveries) {
      const getMultipleDeliveries = async () => {
        const data = await Promise.all(
          skus?.map(async (skuShipment) => {
            const newSlaMessage = await retrieveSlaPerShipment(skuShipment);
            return newSlaMessage;
          }),
        );
        setSlaMessages(data);
      };
      try {
        (async () => {
          await getMultipleDeliveries();
        })();
      } catch (error) {
        return error;
      }
    }
    // default return for es-lint because of if/else
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skusAsJSON, isGroupingSla, product, hasMultipleDeliveries]);

  const productAsJSON = JSON.stringify(product);
  // PDP SLAs
  useEffect(() => {
    let productValue;

    try {
      productValue = JSON.parse(productAsJSON);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useSlaService - Error parsing product: ', error.message);
      productValue = {};
    }

    const retrieveSla = async () => {
      if (!skus?.length) {
        setSlaMessages([]);
      }
      const response = await slaService.getSlas(skus);

      const slaData = isGroupingSla
        ? getGroupedSla({ skus, slaResponse: response, product: productValue })
        : getIndividualSla({
            skus,
            slaResponse: response,
            product: productValue,
            bedframeSelector: bedframeSelectorFromDrupalSettings,
          });
      setSlaMessages(slaData);
    };
    if (!hasMultipleDeliveries) {
      retrieveSla();
    }
  }, [
    skus,
    isGroupingSla,
    hasMultipleDeliveries,
    bedframeSelectorFromDrupalSettings,
    productAsJSON,
  ]);

  return [slaMessages];
};

export default useSlaService;
